import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Outreach, outreachApi, sitesApi } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

const TYPE_LABELS: Record<string, string> = {
  chamber: "Chamber of commerce",
  sponsorship: "Local sponsorship",
  guest_post: "Guest post",
  niche_directory: "Niche directory",
  niche_edit: "Niche edit / link insertion",
  press_release: "Press release",
  other: "Other",
};

export function OutreachPage() {
  const qc = useQueryClient();
  const outreachQ = useQuery({ queryKey: ["outreach"], queryFn: () => outreachApi.list() });
  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: sitesApi.list });
  const [open, setOpen] = useState(false);

  // Roll-ups
  const stats = (outreachQ.data?.outreach ?? []).reduce(
    (acc, o) => {
      acc.total += 1;
      if (o.status === "placed") acc.placed += 1;
      if (o.cost) acc.totalSpend += o.cost;
      return acc;
    },
    { total: 0, placed: 0, totalSpend: 0 },
  );

  const sitesById: Record<string, string> = {};
  for (const s of sitesQ.data?.sites ?? []) sitesById[s.id] = s.domain;
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<Outreach>(
    outreachQ.data?.outreach ?? [],
    {
      target: (o) => o.target,
      type: (o) => o.type,
      site: (o) => o.siteId ? (sitesById[o.siteId] ?? "") : "",
      status: (o) => o.status,
      cost: (o) => o.cost,
      link: (o) => o.linkUrl,
    },
  );

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Outreach</h1>
          <p className="text-ink-600 text-sm mt-1">
            Backlink + citation acquisition pipeline. Each placed link = one closer to ranking.
          </p>
        </div>
        <button onClick={() => setOpen(true)}
                className="px-3 py-1.5 text-sm bg-ink-900 text-white rounded-md hover:bg-ink-800 font-medium">
          + New target
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat label="Total tracked" value={stats.total.toLocaleString()} />
        <Stat label="Placed (live links)" value={stats.placed.toLocaleString()} accent={stats.placed > 0} />
        <Stat label="Total spend" value={`$${stats.totalSpend.toFixed(2)}`} />
      </div>

      {outreachQ.isLoading && <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>}
      {outreachQ.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((outreachQ.error as Error).message)}
        </div>
      )}
      {outreachQ.data && outreachQ.data.outreach.length === 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center">
          <p className="text-ink-700 font-medium">No outreach tracked yet.</p>
          <p className="text-ink-500 text-sm mt-1">
            Add chamber-of-commerce memberships, local sponsorships, guest-post targets, niche
            directories, niche edits, or anything else that could yield a backlink.
          </p>
        </div>
      )}
      {outreachQ.data && outreachQ.data.outreach.length > 0 && (
        <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
              <tr>
                <SortHeader label="Target" sortKey="target" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <SortHeader label="Type" sortKey="type" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <SortHeader label="Status" sortKey="status" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <SortHeader label="Cost" sortKey="cost" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
                <SortHeader label="Link" sortKey="link" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {sortedRows.map((o) => (
                <Row key={o.id} item={o} sites={sitesQ.data?.sites ?? []} onChange={() => qc.invalidateQueries({ queryKey: ["outreach"] })} />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {open && sitesQ.data && (
        <NewOutreachModal
          sites={sitesQ.data.sites.map((s) => ({ id: s.id, domain: s.domain }))}
          onClose={() => setOpen(false)}
          onCreated={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["outreach"] }); }}
        />
      )}
    </div>
  );
}

function Row({ item, sites, onChange }: {
  item: Outreach;
  sites: Array<{ id: string; domain: string }>;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof outreachApi.update>[1]) => outreachApi.update(item.id, patch),
    onSuccess: onChange,
  });
  const tone: Record<string, string> = {
    identified: "bg-blue-100 text-blue-800",
    contacted: "bg-accent-100 text-accent-700",
    replied: "bg-accent-100 text-accent-700",
    placed: "bg-green-100 text-green-800",
    declined: "bg-ink-100 text-ink-500",
    dead: "bg-ink-100 text-ink-500",
  };
  const siteDomain = item.siteId ? sites.find((s) => s.id === item.siteId)?.domain ?? item.siteId.slice(0, 8) + "…" : "(portfolio-wide)";
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-3">
        {item.targetUrl ? (
          <a href={item.targetUrl} target="_blank" rel="noreferrer" className="font-medium text-ink-900 hover:text-accent">
            {item.target} <span aria-hidden>↗</span>
          </a>
        ) : <span className="font-medium text-ink-900">{item.target}</span>}
      </td>
      <td className="px-4 py-3 text-ink-600">{TYPE_LABELS[item.type] ?? item.type}</td>
      <td className="px-4 py-3 text-ink-600 text-xs">{siteDomain}</td>
      <td className="px-4 py-3">
        <select value={item.status} onChange={(e) => update.mutate({ status: e.target.value })}
                disabled={update.isPending}
                className={`px-2 py-0.5 text-xs rounded-md font-medium border-none ${tone[item.status] ?? tone.dead}`}>
          <option value="identified">identified</option>
          <option value="contacted">contacted</option>
          <option value="replied">replied</option>
          <option value="placed">placed</option>
          <option value="declined">declined</option>
          <option value="dead">dead</option>
        </select>
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-600">
        {item.cost != null ? `$${item.cost.toFixed(2)}` : <span className="text-ink-400">—</span>}
      </td>
      <td className="px-4 py-3">
        {item.linkUrl ? (
          <a href={item.linkUrl} target="_blank" rel="noreferrer" className="text-xs text-ink-600 hover:text-accent break-all">
            {item.linkUrl.slice(0, 40)}{item.linkUrl.length > 40 && "…"} <span aria-hidden>↗</span>
          </a>
        ) : <span className="text-ink-400 text-xs">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {update.isPending && <span className="text-xs text-ink-500">saving…</span>}
      </td>
    </tr>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-white border border-ink-100 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</div>
      <div className={`mt-1 text-2xl font-display font-bold tabular-nums ${accent ? "text-accent-600" : "text-ink-900"}`}>
        {value}
      </div>
    </div>
  );
}

function NewOutreachModal({ sites, onClose, onCreated }: {
  sites: Array<{ id: string; domain: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [type, setType] = useState("chamber");
  const [target, setTarget] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [siteId, setSiteId] = useState("");
  const [cost, setCost] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const m = useMutation({
    mutationFn: () => outreachApi.create({
      type, target, targetUrl: targetUrl || undefined,
      siteId: siteId || undefined,
      cost: typeof cost === "number" ? cost : undefined,
      notes: notes || undefined,
    }),
    onSuccess: onCreated,
  });
  const inputCls = "w-full px-3 py-2 border-2 border-ink-100 rounded-lg focus:border-accent focus:outline-none text-sm";
  return (
    <div className="fixed inset-0 bg-ink-900/50 z-50 flex items-center justify-center p-4">
      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Add outreach target</h3>
          <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-900 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <Field label="Type *">
            <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Target *">
            <input value={target} onChange={(e) => setTarget(e.target.value)} required autoFocus
                   placeholder="e.g. Frisco Chamber of Commerce, This Old House contributors"
                   className={inputCls} />
          </Field>
          <Field label="Target URL">
            <input value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)}
                   placeholder="https://…" className={inputCls} />
          </Field>
          <Field label="Site (or leave blank for portfolio-wide)">
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inputCls}>
              <option value="">(portfolio-wide)</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.domain}</option>)}
            </select>
          </Field>
          <Field label="Estimated cost (USD)">
            <input type="number" min={0} step={10} value={cost}
                   onChange={(e) => setCost(e.target.value ? parseFloat(e.target.value) : "")}
                   className={inputCls} />
          </Field>
          <Field label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                      placeholder="Strategy, contact info, deadline" className={inputCls} />
          </Field>
          {m.error && <p className="text-red-700 text-sm">{String((m.error as Error).message)}</p>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={m.isPending}
                    className="flex-1 bg-accent hover:bg-accent-600 disabled:bg-accent-100 text-white font-semibold py-2.5 rounded-lg">
              {m.isPending ? "Adding…" : "Add target"}
            </button>
            <button type="button" onClick={onClose}
                    className="px-4 py-2.5 border border-ink-200 text-ink-700 hover:bg-ink-50 rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-ink-500 font-medium mb-1">{label}</span>
      {children}
    </label>
  );
}
